import { PageCanvas } from "@/components/page-canvas";
import { Form, Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import AppLayout from "@/layouts/app_layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { locationServicePath, locationServicesPath } from "@/routes";
import type { Service } from "./types";

export default function ServiceForm({ service }: { service: Service | null }) {
  const { auth, errors } = usePage().props;
  const { t } = useTranslation();
  if (!auth.location) return null;
  const locationId = auth.location.id;
  const title = t(service ? "services.edit" : "services.add");
  return (
    <AppLayout breadcrumbs={[]}>
      <Head title={title} />
      <PageCanvas>
        <h1 className="text-page font-display">{title}</h1>
        <p className="text-muted-foreground text-sm">
          {t("services.shared_help")}
        </p>
        <Card>
          <CardContent className="pt-6">
            <Form
              key={service?.revision || "new"}
              action={
                service
                  ? locationServicePath(locationId, service.id)
                  : locationServicesPath(locationId)
              }
              method={service ? "patch" : "post"}
            >
              {({ processing }) => (
                <div className="space-y-4">
                  {Object.keys(errors || {}).length > 0 && (
                    <div role="alert" className="text-destructive text-sm">
                      {Object.values(errors).map((error, i) => (
                        <p key={i}>{error}</p>
                      ))}
                    </div>
                  )}
                  {service && (
                    <input
                      type="hidden"
                      name="revision"
                      value={service.revision}
                    />
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="service-name">{t("services.name")}</Label>
                    <Input
                      autoFocus
                      id="service-name"
                      name="service[name]"
                      defaultValue={service?.name || ""}
                      maxLength={200}
                      aria-invalid={!!errors?.name}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="service-description">
                      {t("services.description")}
                    </Label>
                    <Textarea
                      id="service-description"
                      name="service[description]"
                      defaultValue={service?.description || ""}
                      maxLength={10000}
                      aria-invalid={!!errors?.description}
                    />
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <Button type="submit" disabled={processing}>
                      {t("services.save")}
                    </Button>
                    <Button
                      variant="outline"
                      render={<Link href={locationServicesPath(locationId)} />}
                    >
                      {t("customers.cancel")}
                    </Button>
                  </div>
                </div>
              )}
            </Form>
          </CardContent>
        </Card>
      </PageCanvas>
    </AppLayout>
  );
}
